// src/FileTypeIcon.js
// Иконка файла в виде листа с загнутым углом и подписью расширения.
// Цвет подбирается по типу файла: pdf — красный, word — синий и т.д.

const TYPES = [
  { label: 'PDF',  color: '#d93025', ext: ['pdf'] },
  { label: 'DOC',  color: '#2563eb', ext: ['doc', 'docx', 'docm', 'dot', 'dotx', 'odt', 'rtf'] },
  { label: 'XLS',  color: '#16a34a', ext: ['xls', 'xlsx', 'xlsm', 'xlt', 'xltx', 'ods', 'csv'] },
  { label: 'PPT',  color: '#ea580c', ext: ['ppt', 'pptx', 'pptm', 'pot', 'potx', 'odp'] },
  { label: 'IMG',  color: '#7c3aed', ext: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'heic', 'heif', 'tif', 'tiff'] },
  { label: 'VID',  color: '#db2777', ext: ['mp4', 'avi', 'mov', 'mkv', 'webm', 'wmv', 'flv', 'm4v', 'mpg', 'mpeg'] },
  { label: 'AUD',  color: '#0891b2', ext: ['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac', 'wma'] },
  { label: 'ZIP',  color: '#b45309', ext: ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'] },
  { label: 'TXT',  color: '#64748b', ext: ['txt', 'md', 'log', 'rtf'] },
];

const FALLBACK = { label: 'ФАЙЛ', color: '#64748b' };

const EXT_MAP = TYPES.reduce((acc, type) => {
  type.ext.forEach((e) => { acc[e] = type; });
  return acc;
}, {});

// Категории с бэкенда — запасной вариант, если расширения в имени нет
const CATEGORY_MAP = {
  pdf: EXT_MAP.pdf,
  image: EXT_MAP.png,
  video: EXT_MAP.mp4,
  audio: EXT_MAP.mp3,
};

export function getFileExtension(fileName) {
  if (!fileName) return '';
  // Отрезаем возможные параметры и путь, затем берём часть после последней точки
  const clean = String(fileName).split(/[?#]/)[0].split('/').pop();
  const dot = clean.lastIndexOf('.');
  if (dot <= 0 || dot === clean.length - 1) return '';
  return clean.slice(dot + 1).toLowerCase();
}

// Форматы, которые сервер умеет показать без скачивания.
// Список должен совпадать с PREVIEWABLE_EXTENSIONS в core/views.py.
const PREVIEWABLE = new Set([
  'doc', 'docx', 'docm', 'dot', 'dotx', 'odt', 'rtf',
  'xls', 'xlsx', 'xlsm', 'xlt', 'xltx', 'ods', 'csv',
  'ppt', 'pptx', 'pptm', 'pot', 'potx', 'odp',
]);

export function isPreviewable(fileName) {
  return PREVIEWABLE.has(getFileExtension(fileName));
}

export function getFileTypeInfo(fileName, fileType) {
  const ext = getFileExtension(fileName);
  const known = EXT_MAP[ext];
  if (known) {
    // На подписи показываем настоящее расширение (docx, а не doc)
    return { color: known.color, label: ext.toUpperCase() };
  }
  const byCategory = CATEGORY_MAP[fileType];
  if (byCategory) {
    return { color: byCategory.color, label: ext ? ext.toUpperCase() : byCategory.label };
  }
  return { color: FALLBACK.color, label: ext ? ext.toUpperCase() : FALLBACK.label };
}

export default function FileTypeIcon({ fileName, fileType, size = 44, className = '' }) {
  const { color, label } = getFileTypeInfo(fileName, fileType);
  // На плашке помещается не больше четырёх знаков; в aria-label
  // при этом остаётся полное расширение.
  const text = label.length > 4 ? label.slice(0, 4) : label;
  const fontSize = text.length >= 4 ? 8.6 : text.length === 3 ? 10 : 11;

  return (
    <svg
      className={`file-type-icon ${className}`}
      width={size}
      height={size * 1.2}
      viewBox="0 0 40 48"
      role="img"
      aria-label={`Файл ${label}`}
      focusable="false"
    >
      {/* Лист с загнутым уголком */}
      <path
        d="M8 1h18.3c.5 0 1 .2 1.4.6l9.7 9.7c.4.4.6.9.6 1.4V43a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V5a4 4 0 0 1 4-4z"
        fill={color}
      />
      <path d="M26.5 1.2V9a3 3 0 0 0 3 3h7.8z" fill="#ffffff" fillOpacity="0.42" />
      {/* Плашка с расширением */}
      <rect x="4" y="27" width="30" height="13" rx="2.5" fill="#000000" fillOpacity="0.22" />
      <text
        x="19"
        y="36.6"
        textAnchor="middle"
        fill="#ffffff"
        fontSize={fontSize}
        fontWeight="700"
        fontFamily="inherit"
        letterSpacing="0.3"
      >
        {text}
      </text>
    </svg>
  );
}
